import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from '../ListingFieldRichText/ListingFieldRichText.module.css';
import IconCollection from '../IconCollection/IconCollection';
import { Button, Chevron } from '../../components';
import FieldCheckbox from '../FieldCheckbox/FieldCheckbox';
import ReactPlayer from 'react-player';
import IconClose from '../IconClose/IconClose';
import styles from './ListingUseInstructionsField.module.css';
import { Field } from 'react-final-form';

const ACCEPT_VIDEO = 'video/mp4,video/quicktime,video/x-msvideo,video/webm';

const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
const MAX_VIDEO_DURATION = 120; // seconds
const REQUIRED_WIDTH = 1280;
const REQUIRED_HEIGHT = 720;
const REQUIRED_ASPECT = 16 / 9;
const ACCEPTED_MIME = ['video/mp4']; // browser can't reliably check codecs, but we can check mime

const ListingUseInstructionsField = ({
    name,
    validate,
    sectionTitle,
    label = 'How To Use Instructions',
    value = {},
    placeholder = 'Provide instructions for using your item',
    helperText,
    required,
    maxLength = 2000,
    showRegenerate = true,
    onRegenerate,
    regenerateDisabled,
    showCredits = true,
    creditsUsed,
    maxCredits,
    onCreditsInfoClick,
    showChevron = true,
    showToggle = true,
    toggleValue,
    enabled,
    form,
    generateInProgress,
    generateError,
}) => {
    
    const [expanded, setExpanded] = useState(true);
    const [ReactQuill, setReactQuill] = useState(null);
    const quillRef = useRef();
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 767);
    const [videoError, setVideoError] = useState('');
    const charCount = !!value?.text && value?.text?.length > 0 ? value?.text?.replace(/<[^>]+>/g, '').length : 0;

    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            import('react-quill').then((mod) => {
                setReactQuill(() => mod.default);
                import('react-quill/dist/quill.snow.css');
            });
        }
    }, []);

    const handleTextChange = (content, delta, source, editor) => {
        form.change(`${name}.text`, content);
    };

    const handleVideoChange = (e) => {
        setVideoError('');
        const file = e.target.files[0];
        if (!file) return;

        // Check file size
        if (file.size > MAX_VIDEO_SIZE_BYTES) {
            setVideoError(`Video must be less than ${MAX_VIDEO_SIZE_MB}MB.`);
            form.change(`${name}.video`, null);
            return;
        }

        // Check MIME type
        if (!ACCEPTED_MIME.includes(file.type)) {
            setVideoError('Only MP4 videos are allowed.');
            form.change(`${name}.video`, null);
            return;
        }

        // Use <video> to check duration, resolution, aspect
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = url;
        video.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            const duration = video.duration;
            const width = video.videoWidth;
            const height = video.videoHeight;
            const aspect = width / height;
            // Duration
            if (duration > MAX_VIDEO_DURATION) {
                setVideoError('Video must be 2 minutes or less.');
                form.change(`${name}.video`, null);
                return;
            }
            // Resolution
            // if (width !== REQUIRED_WIDTH || height !== REQUIRED_HEIGHT) {
            //     setVideoError('Video resolution must be exactly 1280×720 (720p).');
            //     form.change(`${name}.video`, null);
            //     return;
            // }
            // Aspect ratio (allow small floating point error)
            if (Math.abs(aspect - REQUIRED_ASPECT) > 0.01) {
                setVideoError('Video aspect ratio must be 16:9.');
                form.change(`${name}.video`, null);
                return;
            }
            // If all checks pass
            setVideoError('');
            form.change(`${name}.video`, file);
        };
        video.onerror = () => {
            setVideoError('Invalid video file.');
            form.change(`${name}.video`, null);
        };
    };


    const handleRemoveVideo = () => {
        form.change(`${name}.video`, null);
    };

    React.useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 767);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const hiddenClass = !expanded ? css.bodyCollapsed : '';
    return (
        <div className={css.root}>
            <div className={css.header}>
                <div className={css.headerTitle}>
                    <span className={css.title}>{sectionTitle}</span>

                </div>
                <div className={css.headerActions}>
                    {!isMobile && showRegenerate && (
                        <Button
                            variant="warning"
                            type="button"
                            size={"sm"}
                            className={classNames(css.regenerateBtn, hiddenClass)}
                            onClick={onRegenerate}
                            disabled={regenerateDisabled}
                        >
                            <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                <IconCollection name="icon_regenrate" width={16} height={16} />
                            </span> Regenerate
                        </Button>
                    )}
                    {showToggle && (
                        <div className={css.toggleWrapper}>
                        <label className={css.toggleLabel}>
                            <FieldCheckbox
                                id={`${name}Enabled`}
                                name={`${name}Enabled`}
                                value={toggleValue}
                                label=""
                                className={css.toggleInput}
                            />
                            <span className={classNames(css.toggleSlider, { [css.enabled]: enabled })}></span>
                        </label>
                        <span className={css.toggleLabelText}>Enabled</span>
                        </div>
                    )}

                    {showChevron && (
                        <button
                            type="button"
                            className={css.toggleBtn}
                            onClick={() => setExpanded(e => !e)}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                            <Chevron expanded={expanded} width={24} height={24} />
                        </button>
                    )}
                </div>
            </div>
            <span className={css.label}>{label}{required && <span className={css.required}>*</span>}</span>
            <div className={expanded ? css.body : css.bodyCollapsed}>
                {/* Rich Text Instructions */}
                <div className={css.inputRow}>
                    {ReactQuill ? (
                        <Field name={`${name}.text`} validate={validate}>
                            {({ input, meta }) => {
                                return (
                                    <div className={css.richTextField}>
                                        <ReactQuill
                                            ref={quillRef}
                                            className={css.richText}
                                            theme="snow"
                                            value={value.text}
                                            onChange={handleTextChange}
                                            onBlur={input.onBlur}
                                            placeholder={placeholder}
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline'],
                                                    [{ 'size': [] }],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    [{ 'align': [] }],
                                                    ['blockquote'],
                                                    ['link'],
                                                    ['clean'],
                                                ],
                                            }}
                                            formats={[
                                                'bold', 'italic', 'underline',
                                                'size',
                                                'list', 'bullet',
                                                'align',
                                                'blockquote',
                                                'link',
                                            ]}
                                        />
                                        {meta.error && meta.touched && <div className={css.error}>{meta.error}</div>}
                                    </div>
                                )
                            }}
                        </Field>
                    ) : (
                        <div style={{ minHeight: 180, width: '100%' }} />
                    )}
                    <span className={css.charCounter}>{charCount}/{maxLength}</span>
                </div>
                {!!helperText && <div className={css.helperText}>{helperText}</div>}
                {/* Video Instructions */}
                <div style={{ marginTop: 24 }}>
                    <div className={css.sectionTitle}>Instructional Video <span style={{ color: '#ddd', fontWeight: 500 }}>(optional)</span></div>
                    <div className={css.label}>
                        Upload a video showing how to use your item.
                    </div>
                    <div>
                        {!value?.video && (
                            <Field type="file" name={`${name}.video`}>
                                {({ input }) => (
                                    <label htmlFor={`${input.name}-video-upload`} className={styles.uploadLabel}>
                                        <span className={styles.uploadIcon} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <IconCollection name="image_upload" width={24} height={24} />
                                        </span>
                                        <div>
                                            <span className={styles.uploadText}><span className={styles.uploadTextBold}>Click to upload</span> or drag and drop</span>
                                        </div>
                                        <span className={styles.uploadHint}>MP4 (H.264 + AAC), max 50MB, 2min, 1280×720, 16:9</span>
                                        <input
                                            id={`${input.name}-video-upload`}
                                            name={input.name}
                                            type={input.type}
                                            accept={ACCEPT_VIDEO}
                                            style={{ display: 'none' }}
                                            onChange={handleVideoChange}
                                        />
                                        {videoError && <div className={css.error} style={{marginTop:8}}>{videoError}</div>}
                                    </label>
                                )}
                            </Field>
                        )}
                        {value?.video && (
                            <div style={{ position: 'relative', display: 'inline-block', maxWidth: 280 }} className={styles.videoPreview}>
                                <ReactPlayer
                                    url={value?.video instanceof File ? URL.createObjectURL(value?.video) : value?.video?.playback_url}
                                    controls
                                    width={280}
                                    height={180}
                                    style={{ borderRadius: 8, background: '#000' }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveVideo}
                                    className={styles.removeVideoBtn}
                                    style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(255,255,255,0.7)', borderRadius: '50%' }}
                                    aria-label="Remove video"
                                >
                                    <IconClose width={20} height={20} />
                                </button>
                            </div>
                        )}
                    </div>

                </div>
                <div className={classNames(css.mobileActions, hiddenClass)}>
                    {isMobile && showRegenerate && (
                        <Button
                            variant="warning"
                            type="button"
                            size={"sm"}
                            className={classNames(css.regenerateBtn, hiddenClass)}
                            onClick={onRegenerate}
                            disabled={regenerateDisabled}
                        >
                            <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                <IconCollection name="icon_regenrate" width={16} height={16} />
                            </span> Regenerate
                        </Button>
                    )}
                    {showCredits && <div className={css.creditsRow}>
                        <span className={css.creditsText}>{creditsUsed}/{maxCredits} credits used</span>
                        {typeof onCreditsInfoClick === 'function' && (
                            <button type="button" className={css.creditsInfoBtn} onClick={onCreditsInfoClick}>
                                <IconCollection name="info_icon" width={16} height={16} />
                            </button>
                        )}
                    </div>}
                </div>
            </div>
        </div>
    );
};

ListingUseInstructionsField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.shape({
        text: PropTypes.string,
        video: PropTypes.any,
    }),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    showRegenerate: PropTypes.bool,
    onRegenerate: PropTypes.func,
    regenerateDisabled: PropTypes.bool,
    showCredits: PropTypes.bool,
    creditsUsed: PropTypes.number,
    maxCredits: PropTypes.number,
    onCreditsInfoClick: PropTypes.func,
    showChevron: PropTypes.bool,
    showToggle: PropTypes.bool,
    toggleValue: PropTypes.string,
    enabled: PropTypes.bool,
    form: PropTypes.object,
    validate: PropTypes.func,
};

export default ListingUseInstructionsField; 